import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AppInitializer, AuthErrorHandlerInitializer } from './components/AppInitializer';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute, PublicRoute } from './components/auth/ProtectedRoute';
import { AuthenticatedProviders } from './components/auth/AuthenticatedProviders';
import Layout from './components/Layout';
import ChatPage from './pages/ChatPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import KnowledgeBaseFilesPage from './pages/KnowledgeBaseFilesPage';
import HomePage from './pages/HomePage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import UserManagementPage from './pages/UserManagementPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import LLMConfigPage from './pages/admin/LLMConfigPage';
import ResourcesPage from './pages/admin/ResourcesPage';
import NotFoundPage from './pages/NotFoundPage';

const App: React.FC = () => {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppInitializer>
        <AuthProvider>
          <AuthErrorHandlerInitializer>
            <div className='App'>
              <Routes>
                {/* Public Routes */}
                <Route
                  path='/login'
                  element={
                    <PublicRoute>
                      <LoginPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path='/register'
                  element={
                    <PublicRoute>
                      <RegisterPage />
                    </PublicRoute>
                  }
                />

                {/* Protected Routes - All authenticated routes share the same providers */}
                <Route
                  path='/'
                  element={
                    <ProtectedRoute>
                      <AuthenticatedProviders>
                        <Layout />
                      </AuthenticatedProviders>
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<HomePage />} />
                  <Route path='chat' element={<ChatPage />} />
                  <Route path='chat/:conversationId' element={<ChatPage />} />
                  <Route path='knowledge' element={<KnowledgeBasePage />} />
                  <Route path='settings' element={<SettingsPage />} />

                  {/* Admin-only routes */}
                  <Route
                    path='admin'
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path='admin/users'
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <UserManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path='admin/llm-config'
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <LLMConfigPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path='admin/resources'
                    element={
                      <ProtectedRoute requiredRoles={['admin']}>
                        <ResourcesPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* Knowledge Base Files Route - Custom Layout */}
                <Route
                  path='knowledge/:kbId/files'
                  element={
                    <ProtectedRoute>
                      <AuthenticatedProviders>
                        <KnowledgeBaseFilesPage />
                      </AuthenticatedProviders>
                    </ProtectedRoute>
                  }
                />

                {/* Fallback route */}
                <Route
                  path='*'
                  element={
                    <ProtectedRoute>
                      <AuthenticatedProviders>
                        <Layout />
                      </AuthenticatedProviders>
                    </ProtectedRoute>
                  }
                >
                  <Route path='*' element={<NotFoundPage />} />
                </Route>
              </Routes>
              <Toaster position='top-right' richColors />
            </div>
          </AuthErrorHandlerInitializer>
        </AuthProvider>
      </AppInitializer>
    </Router>
  );
};

export default App;
